import { singleFinalDot } from '../../utils/sentenceEnd';
import type { TFunction } from 'i18next';
import { formatMoneyPl } from '../../utils/currency';
import { formatLocalizedQuantity } from '../../utils/numberFormatting';
import { formatUnit } from '../../utils/units';
import type { FeedbackApi } from '../../utils/feedback';
import {
  readMissingDimensionLabels,
  readMissingGoodsValueLabels,
  readMixedUnitsWarning,
  readCountedInPiecesOnlyLabels,
} from './transportCostAllocationPresentation';
import { staleTransportSplitWarning } from '../orders/stockValuationSaveWarning';

const priceText = (value: unknown, currency: unknown): string | null => {
  if (value === null || value === undefined || value === '') return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? formatMoneyPl(amount, String(currency || 'PLN')) : null;
};

/**
 * „ZK/2: 400,00 PLN, ZK/3: 600,00 PLN” — the recalculated shares of the
 * customer orders left on a truck (round 2, U11). Empty when none is known.
 */
export function describeRemainingShares(route: {
  routeOrders?: Array<{ order?: { displayOrderNumber?: string; orderNumber?: string } | null; orderId: string; allocatedTransportCost?: number | null }>;
} | null | undefined): string {
  return (route?.routeOrders || [])
    .filter((row) => row.allocatedTransportCost !== null && row.allocatedTransportCost !== undefined)
    .map((row) => `${row.order?.displayOrderNumber || row.order?.orderNumber || row.orderId}: ${formatMoneyPl(Number(row.allocatedTransportCost), 'PLN')}`)
    .join(', ');
}

/**
 * „Koszt auta: było X → jest Y” after an edit that changed the price or its
 * currency (usability round 2026-10-05); null when nothing changed.
 */
export function describeTransportPriceChange(
  before: { transportRate?: unknown; transportCost?: unknown; currency?: unknown } | null | undefined,
  after: unknown,
): { before: string; after: string } | null {
  const saved = (after || {}) as { transportRate?: unknown; currency?: unknown };
  const beforeText = priceText(before?.transportRate ?? before?.transportCost, before?.currency) ?? '—';
  const afterText = priceText(saved.transportRate, saved.currency) ?? '—';
  return beforeText === afterText ? null : { before: beforeText, after: afterText };
}

type PairingWarningRow = {
  code?: unknown;
  label?: unknown;
  orderId?: unknown;
  productName?: unknown;
  supplierProductName?: unknown;
  unit?: unknown;
  carried?: unknown;
  paired?: unknown;
  surplus?: unknown;
};

/**
 * PR3 (decision 2026-10-06): one Polish sentence per ZK ↔ ZD pairing warning
 * of a save (backend utils/transportCargoPairing.js; informational, WP1).
 * `label` is the business number the backend attached (ZK for an unpaired
 * line, ZD for a ZD line). Unpaired lines are listed in one sentence.
 */
export function describeCargoPairingWarnings(responseData: unknown, t: TFunction, language = 'pl'): string[] {
  const raw = (responseData as { allocationWarnings?: unknown } | null)?.allocationWarnings;
  const rows = (Array.isArray(raw) ? raw : []).map((row) => (row || {}) as PairingWarningRow);
  const text = (value: unknown) => (value === null || value === undefined ? '' : String(value).trim());
  const subject = (row: PairingWarningRow) => {
    const product = text(row.productName) || t('transport:pairing.warnings.unknownProduct');
    const label = text(row.label) || text(row.orderId);
    return label ? `${product} (${label})` : product;
  };
  const quantity = (value: unknown, unit: unknown) => (
    `${formatLocalizedQuantity(Number(value), language)} ${formatUnit(text(unit) || 'pcs', language)}`
  );
  const messages: string[] = [];
  const unpaired = rows.filter((row) => row.code === 'TRANSPORT_CARGO_UNPAIRED').map(subject);
  if (unpaired.length > 0) messages.push(t('transport:pairing.warnings.unpaired', { items: unpaired.join(', ') }));
  rows.forEach((row) => {
    if (row.code === 'TRANSPORT_CARGO_PAIRING_VARIANT_MISMATCH') {
      messages.push(t('transport:pairing.warnings.variantMismatch', {
        product: text(row.productName) || t('transport:pairing.warnings.unknownProduct'),
        supplierProduct: text(row.supplierProductName) || t('transport:pairing.warnings.unknownProduct'),
        number: text(row.label) || '—',
      }));
    } else if (row.code === 'TRANSPORT_CARGO_PAIRING_OVER_CARRIED') {
      messages.push(t('transport:pairing.warnings.overCarried', {
        product: subject(row),
        carried: quantity(row.carried, row.unit),
        paired: quantity(row.paired, row.unit),
      }));
    } else if (row.code === 'TRANSPORT_SUPPLIER_CARGO_NOT_DELIVERED') {
      // The surplus is what neither pairs nor unpaired cargo take (review N1, as on the card).
      const surplus = row.surplus ?? Math.max(0, Number(row.carried) - Number(row.paired));
      messages.push(t('transport:pairing.warnings.notDelivered', {
        product: subject(row),
        carried: quantity(row.carried, row.unit),
        paired: quantity(row.paired, row.unit),
        surplus: quantity(surplus, row.unit),
      }));
    }
  });
  return messages.map(singleFinalDot);
}

/**
 * After a transport save: the cost-split warnings of that save (decision
 * 2026-09-30 — the key never blocks, positions it cannot weigh get 0) and the
 * other trucks of the same ZD whose re-split failed. One home for the create
 * and the edit page. Cost warnings only for roles that edit transport costs.
 */
export function showTransportSaveWarnings(
  responseData: unknown,
  feedback: FeedbackApi,
  t: TFunction,
  canEditTransportCosts: boolean,
): void {
  if (canEditTransportCosts) {
    const missingDimensionLabels = readMissingDimensionLabels(responseData);
    if (missingDimensionLabels.length > 0) {
      feedback.warning(
        t('transport:allocation.missingDimensionsSaved', { items: missingDimensionLabels.join(', ') }),
        { sticky: true },
      );
    }
    const missingValueLabels = readMissingGoodsValueLabels(responseData);
    if (missingValueLabels.length > 0) {
      feedback.warning(
        t('transport:allocation.missingGoodsValueSaved', { items: missingValueLabels.join(', ') }),
        { sticky: true },
      );
    }
    const piecesOnlyLabels = readCountedInPiecesOnlyLabels(responseData);
    if (piecesOnlyLabels.length > 0) {
      feedback.warning(
        t('transport:allocation.countedInPiecesOnlySaved', { items: piecesOnlyLabels.join(', ') }),
        { sticky: true },
      );
    }
    if (readMixedUnitsWarning(responseData)) {
      feedback.warning(t('transport:allocation.mixedUnitsSaved'), { sticky: true });
    }
  }
  // Pairing warnings concern goods, not money: every role that receives them.
  describeCargoPairingWarnings(responseData, t).forEach((message) => {
    feedback.warning(message, { sticky: true });
  });
  const staleSplit = staleTransportSplitWarning(responseData, t);
  if (staleSplit) feedback.warning(staleSplit, { sticky: true });
}
